import { useSuspenseQuery } from '@tanstack/react-query';
import { CircleDollarSign, UserRoundCheck, UserRoundPlus } from 'lucide-react';
import { useIntl } from 'react-intl';

import { ConversionStats } from '@/components/ui/conversion-stats';
import { MetricGroup, MetricGroupItem } from '@/components/ui/metric-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import { useCampaignsMetricsQueryOptions } from './use-campaigns-metrics-query';

export function CampaignsMetricsContent() {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { data } = useSuspenseQuery(useCampaignsMetricsQueryOptions());

  const spendCostHeaderLabel = intl.formatMessage(
    {
      defaultMessage:
        'Based on {applicantCount} applicants across {channelCount} campaigns',
      description:
        'Contextual caption on the spend and cost metrics card, showing how many applicants and channels the totals below are drawn from',
    },
    {
      applicantCount: data.caption.applicant_count,
      channelCount: data.caption.channel_count,
    },
  );

  return (
    <div className="shrink-0 overflow-x-auto">
      <div className="flex min-w-max flex-row items-stretch gap-4 pb-2">
        {/* Sourced this period */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Heading for a metric group showing applicants and hires that came from sourcing campaigns in the current period',
          })}
          icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          tooltipTitle={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Introduction to an explanation of which applicants and hires are included in the sourced period count',
          })}
          tooltipBody={intl.formatMessage({
            defaultMessage:
              'Applicants and hires limited to people you sourced in this period. This is the population your campaign activity drove.',
            description:
              'Explanation that this group counts only applicants and hires from people sourced through campaigns in this period',
          })}
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Number of people who applied through sourcing campaigns in the period',
            })}
          >
            <Typography variant="large2">{data.applicants || '-'}</Typography>
          </MetricGroupItem>

          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Number of people hired who were sourced through campaigns in the period',
            })}
          >
            <Typography variant="large2">{data.hires || '-'}</Typography>
          </MetricGroupItem>

          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Conv. To Hire',
              description:
                'Conversion rate from sourced applicant to hire, expressed as a percentage',
            })}
          >
            <ConversionStats
              stats={{ actual: data.conversion_to_hire, expected: null }}
              typographyVariant="large2"
              showOnlyActual
            />
          </MetricGroupItem>
        </MetricGroup>

        {/* Spend & cost metrics */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Spend & cost metrics',
            description:
              'Heading for a metric group showing spend, cost per applicant, and cost per hire for sourcing campaigns',
          })}
          icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          headerLabel={spendCostHeaderLabel}
          tooltipTitle={intl.formatMessage({
            defaultMessage: 'Spend & cost metrics',
            description:
              'Introduction to an explanation of what the spend and cost metrics card measures',
          })}
          tooltipBody={intl.formatMessage(
            {
              defaultMessage:
                'These are spend and cost metrics for the {channelCount} campaigns posted in Fountain, which together acquired ~{applicantCount} applicants. Cost divides spend by applicants who came through those integrated campaigns.',
              description:
                'Explanation of how spend and cost metrics are calculated: {channelCount} is the number of campaigns and {applicantCount} is the approximate number of applicants they acquired',
            },
            {
              channelCount: data.caption.channel_count,
              applicantCount: data.caption.applicant_count,
            },
          )}
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Spend',
              description: 'Total amount of money spent on sourcing campaigns',
            })}
          >
            <Typography variant="large2">
              {formatCurrency(data.total_spend)}
            </Typography>
          </MetricGroupItem>

          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPA',
              description:
                'Average cost per applicant across sourcing campaigns (CPA = cost per applicant)',
            })}
          >
            <Typography variant="large2">
              {data.avg_cpa != null ? formatCurrency(data.avg_cpa) : '-'}
            </Typography>
          </MetricGroupItem>

          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPH',
              description:
                'Average cost per hire across sourcing campaigns (CPH = cost per hire)',
            })}
          >
            <Typography variant="large2">
              {data.avg_cph != null ? formatCurrency(data.avg_cph) : '-'}
            </Typography>
          </MetricGroupItem>
        </MetricGroup>
      </div>
    </div>
  );
}

export function CampaignsMetricsSkeleton() {
  const intl = useIntl();

  return (
    <div className="shrink-0 overflow-x-auto">
      <div className="flex min-w-max flex-row items-stretch gap-4 pb-2">
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Heading for a metric group showing applicants and hires that came from sourcing campaigns in the current period',
          })}
          icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Number of people who applied through sourcing campaigns in the period',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Number of people hired who were sourced through campaigns in the period',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Conv. To Hire',
              description:
                'Conversion rate from sourced applicant to hire, expressed as a percentage',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
        </MetricGroup>

        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Spend & cost metrics',
            description:
              'Heading for a metric group showing spend, cost per applicant, and cost per hire for sourcing campaigns',
          })}
          icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Spend',
              description: 'Total amount of money spent on sourcing campaigns',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPA',
              description:
                'Average cost per applicant across sourcing campaigns (CPA = cost per applicant)',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPH',
              description:
                'Average cost per hire across sourcing campaigns (CPH = cost per hire)',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
        </MetricGroup>
      </div>
    </div>
  );
}
